// ACCTEXPORT1 (docs/build-briefs/ACCTEXPORT1-quickbooks-connection.md; DEC-514). The pure rules behind the "Accounting (QuickBooks)"
// card in Settings: what the connection chip says, which one action shows, the words and tone of each day's status, and the marker
// QuickBooks's redirect leaves on the app root. Labels and states only (the owner's copy rule: two sentences at most, no em dash,
// "location" for a place). No DOM. Nothing here knows a token, a code or a company id: the server sends none.

import type { AccountingDayDto, AccountingEntryStatusDto, AccountingStatusDto } from "./api.ts";
import { usDate } from "./dateText.ts";
import { exactDollars } from "./franchisorListModel.ts";

export type ChipTone = "good" | "info" | "bad" | "warn" | "neutral";

export interface Chip {
  /** Symbol plus words, never color alone. */
  symbol: string;
  label: string;
  tone: ChipTone;
}

export type ConnectionAction = "connect" | "reconnect" | "disconnect";

export interface ConnectionView {
  chip: Chip;
  /** The company's name when connected, else the one line saying what to do. */
  caption: string;
  action: { kind: ConnectionAction; label: string };
  /** True when the account pickers and the recent days belong on the card. */
  connected: boolean;
}

export function connectionView(status: AccountingStatusDto["connection"]): ConnectionView {
  if (status.status === "connected") {
    return {
      chip: { symbol: "✓", label: "Connected", tone: "good" },
      caption: status.companyName === null ? "QuickBooks company" : status.companyName,
      action: { kind: "disconnect", label: "Disconnect" },
      connected: true,
    };
  }
  if (status.status === "needs_reconnect") {
    return {
      chip: { symbol: "!", label: "Sign in again", tone: "warn" },
      caption: "QuickBooks needs you to sign in again before more days can be sent.",
      action: { kind: "reconnect", label: "Connect again" },
      connected: false,
    };
  }
  return {
    chip: { symbol: "", label: "Not connected", tone: "neutral" },
    caption: "Connect QuickBooks to send each closed day to your books.",
    action: { kind: "connect", label: "Connect QuickBooks" },
    connected: false,
  };
}

const STATUS_CHIPS: Readonly<Record<AccountingEntryStatusDto, Chip>> = {
  sent: { symbol: "✓", label: "Sent", tone: "good" },
  sending: { symbol: "", label: "Sending", tone: "info" },
  will_retry: { symbol: "!", label: "Will retry", tone: "warn" },
  needs_attention: { symbol: "!", label: "Needs attention", tone: "bad" },
  waiting: { symbol: "", label: "Waiting", tone: "neutral" },
  nothing: { symbol: "", label: "Nothing to send", tone: "neutral" },
  not_applicable: { symbol: "", label: "", tone: "neutral" },
};

export function statusChip(status: AccountingEntryStatusDto): Chip {
  return STATUS_CHIPS[status];
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** "2026-10-20" -> "Tue Oct 20". Never an ISO date. */
export function dayLabel(businessDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(businessDate);
  if (match === null) return "";
  const weekday =
    WEEKDAYS[
      new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay()
    ];
  const long = usDate(businessDate);
  // usDate is "Oct 20, 2026"; the table shows "Tue Oct 20" and leaves the year to the page's date context.
  return weekday === undefined || long === ""
    ? ""
    : `${weekday} ${long.slice(0, long.indexOf(","))}`;
}

export interface DayRowView {
  businessDate: string;
  day: string;
  sales: string;
  tax: string;
  tips: string;
  chip: Chip;
  /** One muted line under the chip: why, in plain words, or the fee entry's own state. Empty when there is nothing to add. */
  detail: string;
  /** The Send or Retry action, or null when pressing it could do nothing. */
  action: { label: string } | null;
}

function feesLine(day: AccountingDayDto): string {
  switch (day.fees.status) {
    case "sent":
      return "Card fees sent";
    case "waiting":
      return day.fees.reason ?? "Card fees will send with this day";
    case "will_retry":
    case "needs_attention":
      return day.fees.reason === null
        ? "Card fees need attention"
        : `Card fees: ${day.fees.reason}`;
    case "sending":
      return "Card fees sending";
    default:
      return "";
  }
}

export function dayRows(days: readonly AccountingDayDto[], canManage: boolean): DayRowView[] {
  return days.map((day) => {
    const salesReason = day.sales.reason ?? "";
    const retry = day.sales.status === "will_retry" || day.sales.status === "needs_attention";
    return {
      businessDate: day.businessDate,
      day: dayLabel(day.businessDate),
      // A day with nothing to send shows blank money, never $0.00.
      sales: day.sales.status === "nothing" ? "" : exactDollars(day.salesBeforeTaxCents),
      tax: day.sales.status === "nothing" ? "" : exactDollars(day.taxCents),
      tips: day.sales.status === "nothing" ? "" : exactDollars(day.tipsCents),
      chip: statusChip(day.sales.status),
      detail: salesReason !== "" ? salesReason : feesLine(day),
      action:
        canManage && day.canSend
          ? {
              label:
                retry || day.fees.status === "will_retry" || day.fees.status === "needs_attention"
                  ? "Retry"
                  : "Send",
            }
          : null,
    };
  });
}

/** What QuickBooks's redirect back to the app root left behind: one outcome word, nothing from the provider. */
export type AccountingArrival = "connected" | "denied" | "failed" | "invalid";

export function readAccountingArrival(search: string): AccountingArrival | undefined {
  const value = new URLSearchParams(search).get("accounting");
  return value === "connected" || value === "denied" || value === "failed" || value === "invalid"
    ? value
    : undefined;
}

/** The same address with the marker removed (every other key, and the hash, kept). */
export function withoutAccountingArrival(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("accounting");
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}

export const ARRIVAL_TEXT: Readonly<Record<AccountingArrival, string>> = {
  connected: "QuickBooks is connected.",
  denied: "QuickBooks was not connected. You chose not to allow it.",
  failed: "QuickBooks could not be connected. Nothing was changed. Try again.",
  invalid: "That QuickBooks link has expired. Choose Connect to start again.",
};
